import * as signalR from "@microsoft/signalr";
import { PropsWithChildren, useEffect, useState } from "react";
import {
  LiveTrackTokenApiFactory,
  LiveTrackVehicleDto,
} from "../../../common/data/service-proxies";
import { useAxiosInstance } from "../../../common/hooks/useAxiosInstance";
import { parseJwt } from "../../../common/util/parseJwt";
import { LiveTrackEvent } from "../types";

interface Props {
  vehicleKey?: string;
  tenantId?: string;
  handleLocationUpdated: (args: LiveTrackEvent) => void;
  setError: (error: string) => void;
  setLoading: (loading: boolean) => void;
  setVehicle: (vehicle: LiveTrackVehicleDto) => void;
}

export const AnonymousLocationLiveTrack = ({
  vehicleKey,
  tenantId,
  children,
  handleLocationUpdated,
  setError,
  setLoading,
  setVehicle,
}: PropsWithChildren<Props>) => {
  const [hubAccessToken, setHubAccessToken] = useState<string>();
  const [requestedVehicleId, setRequestedVehicleId] = useState<number>();
  const axios = useAxiosInstance();

  useEffect(() => {
    if (!hubAccessToken || !requestedVehicleId) {
      return;
    }

    LiveTrackTokenApiFactory(
      undefined,
      process.env["REACT_APP_API_URL"]?.replace(/\/+$/, "") ??
        "https://localhost:44327"
    )
      .apiAppLivetracktokenIdVehicleforlivetrackGet(requestedVehicleId, {
        headers: { Authorization: "Bearer " + hubAccessToken },
      })
      .then((response) => {
        if (response.data) {
          setVehicle(response.data);
        }
      })
      .catch((e) =>
        console.error("Unable to get the requested vehicle data", e)
      );
  }, [axios, hubAccessToken, requestedVehicleId, setVehicle]);

  useEffect(() => {
    if (hubAccessToken) {
      return;
    }

    if (vehicleKey && tenantId) {
      LiveTrackTokenApiFactory(
        undefined,
        process.env["REACT_APP_API_URL"]?.replace(/\/+$/, "")
      )
        .apiTenantIdIdGet(tenantId, vehicleKey)
        .then((result) => {
          if (result.data?.token) {
            setHubAccessToken(result.data.token);
            const parsed = parseJwt(result.data.token);
            if (parsed?.sub) {
              setRequestedVehicleId(parsed.sub);
            }
          }
        })
        .catch((e) => {
          setLoading(false);
          setError(!!e.message ? e.message : e);
        });
    }
  }, [vehicleKey, tenantId, hubAccessToken, setError, setLoading]);

  useEffect(() => {
    if (!hubAccessToken || !requestedVehicleId) {
      return;
    }

    const apiUrl =
      process.env["REACT_APP_API_URL"]?.replace(/\/+$/, "") ??
      "https://localhost:44327";
    const hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(`${apiUrl}/signalr-hubs/live-track-hub`, {
        accessTokenFactory: () => hubAccessToken,
      })
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Information)
      .build();

    hubConnection.on("updateLocation", handleLocationUpdated);
    hubConnection.onreconnected(() => {
      if (requestedVehicleId) {
        hubConnection
          .send("setVehicleId", requestedVehicleId)
          .catch((e) =>
            console.warn(
              `Unable to set the vehicle ID to ${requestedVehicleId}`,
              e
            )
          );
      }
    });

    hubConnection
      .start()
      .then(() => {
        if (requestedVehicleId) {
          hubConnection
            .send("setVehicleId", requestedVehicleId)
            .catch((e) =>
              console.warn(
                `Unable to set the vehicle ID to ${requestedVehicleId}`,
                e
              )
            );
        }
      })
      .catch((e) => {
        console.error(e);
        setError(!!e.message ? e.message.toString() : e.toString());
        setLoading(false);
      });

    return () => {
      hubConnection?.off("updateLocation");
      hubConnection.stop();
    };
  }, [
    hubAccessToken,
    handleLocationUpdated,
    requestedVehicleId,
    setError,
    setLoading,
  ]);

  return <>{children}</>;
};
